import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { getOwner } from '@ember/owner';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { and, gt } from 'ember-truth-helpers';
import can from 'ember-can/helpers/can';
import {
  HdsPageHeader,
  HdsLinkInline,
  HdsTextDisplay,
  HdsTextBody,
} from '@hashicorp/design-system-components/components';
import {
  CoreLayoutMain,
  BreadcrumbsContainer,
  Flex,
  CoreModalConfirm,
  Typography,
} from 'core/components';
import { boxMargin, boxPadding } from 'core/modifiers';

import ListTable from 'hcp/components/service-principal/workload-identity-providers/list-table';
import Cards from 'hcp/components/service-principal/workload-identity-providers/cards';

/**
 * Contains the `servicePrincipal` , 'project' and 'workloadIdentityProviders'.
 * @argument model
 * @type {Object}
 */
/**
 *
 * `PageServicePrincipalsProjectDetailWorkloadIdentityProviders` displays the workload identity providers of a project Service Principal and allows for them to be deleted.
 *  It also renders the <ServicePrincipals::WorkloadIdentityProviderCards /> which renders the provider card for each provider that is supported on HCP.
 *
 *
 * ```
 * <Page::ServicePrincipals::Project::Detail::WorkloadIdentityProviders
 *   @model={{model}}
 * />
 * ```
 *
 * @class PageServicePrincipalsProjectDetailWorkloadIdentityProviders
 *
 */
export default class PageServicePrincipalsProjectDetailWorkloadIdentityProviders extends Component {
  @service api;
  @service flashMessages;
  @service intl;
  @service router;

  @tracked showDeleteWipModal = false;
  @tracked servicePrincipalWipToDelete = null;

  @tracked returnFocusTo = null;

  confirmDeleteServicePrincipalWip = (servicePrincipalWip) => {
    this.showDeleteWipModal = true;
    this.servicePrincipalWipToDelete = servicePrincipalWip;
  };

  deleteServicePrincipalWip = task(async () => {
    this.servicePrincipalWipToDelete;
    const response =
      await this.api.servicePrincipal.servicePrincipalsServiceDeleteWorkloadIdentityProvider(
        this.servicePrincipalWipToDelete.resourceName
      );

    this.onDeleteServicePrincipalWipSuccess({
      response,
      servicePrincipalWip: this.servicePrincipalWipToDelete,
    });
  });

  onDeleteServicePrincipalWipSuccess = () => {
    this.resetDeleteServicePrincipalWip();
    this.refreshRoute();
    this.flashMessages.success(
      this.intl.t(
        'components.page.access-control.service-principals.details.workload-identity-providers.delete-provider-modal.success'
      )
    );
  };

  resetDeleteServicePrincipalWip = () => {
    this.showDeleteWipModal = false;
    this.servicePrincipalWipToDelete = null;
  };

  refreshRoute() {
    let route = getOwner(this).lookup(
      `route:${this.router.currentRoute.parent.name}`
    );
    return route.refresh();
  }

  <template>
    <CoreLayoutMain>
      <:header>
        <HdsPageHeader as |PH|>
          <PH.Breadcrumb>
            <BreadcrumbsContainer />
          </PH.Breadcrumb>
          <PH.Title data-test-service-principal-wip-title>
            {{t
              "components.page.access-control.service-principals.details.workload-identity-providers.header.title"
            }}
          </PH.Title>
          <PH.Description>
            {{t
              "components.page.access-control.service-principals.details.workload-identity-providers.header.description"
              htmlSafe=true
            }}
            <HdsLinkInline
              @color="secondary"
              @href="https://developer.hashicorp.com/hcp/docs/hcp/iam/service-principal/workload-identity-federation"
              data-test-service-principal-wip-list-page-learn-more-link
            >
              {{t
                "components.page.access-control.service-principals.details.workload-identity-providers.header.action.learn-more"
              }}
            </HdsLinkInline>
          </PH.Description>
        </HdsPageHeader>
      </:header>
      <:body>
        {{#if
          (and
            (can "list workload-identity-providers")
            (gt @model.workloadIdentityProviders.length 0)
          )
        }}
          <div
            {{boxMargin "0 0 lg 0"}}
            data-test-service-principal-wip-list-table
          >
            <ListTable
              @workloadIdentityProviders={{@model.workloadIdentityProviders}}
              @organizationName={{@model.organization.name}}
              @servicePrincipalName={{@model.servicePrincipal.name}}
              @projectName={{@model.project.name}}
              @handleConfirmDeleteWip={{this.confirmDeleteServicePrincipalWip}}
            />
          </div>
        {{/if}}
        <Flex @direction="column" @gap="sm">
          <HdsTextDisplay
            @tag="h2"
            @size="400"
            data-test-service-principal-wip-subtitle
          >
            {{t
              "components.page.access-control.service-principals.details.workload-identity-providers.sub-header.title"
            }}
          </HdsTextDisplay>
          <HdsTextBody @tag="p">
            {{t
              "components.page.access-control.service-principals.details.workload-identity-providers.sub-header.description"
            }}
          </HdsTextBody>
          <div
            class="workloadIdentityProviderCards__row"
            data-test-service-principal-wip-cards
            {{boxMargin "sm 0 0 0"}}
          >
            <Cards />
          </div>
        </Flex>

        {{#if this.showDeleteWipModal}}
          <CoreModalConfirm
            @confirmHelp={{t
              "components.page.access-control.service-principals.details.workload-identity-providers.delete-provider-modal.helper-text"
              htmlSafe=true
            }}
            @confirmText={{t "components.core.modal.confirm.delete"}}
            @headerIcon="alert-diamond"
            @headerText={{t
              "components.page.access-control.service-principals.details.workload-identity-providers.delete-provider-modal.title"
              wipDisplayName=this.servicePrincipalWipToDelete.displayName
              htmlSafe=true
            }}
            @onCancel={{this.resetDeleteServicePrincipalWip}}
            @onSubmit={{this.deleteServicePrincipalWip}}
          >
            <:body>
              <Typography
                @variant="body1"
                class="hds-typography-body-200 hds-font-weight-regular hds-foreground-primary"
                {{boxPadding "0 0 lg 0"}}
              >
                {{t
                  "components.page.access-control.service-principals.details.workload-identity-providers.delete-provider-modal.body"
                  wipDisplayName=this.servicePrincipalWipToDelete.displayName
                  servicePrincipalName=@model.servicePrincipal.name
                  htmlSafe=true
                }}
              </Typography>
            </:body>
          </CoreModalConfirm>
        {{/if}}
      </:body>
    </CoreLayoutMain>
  </template>
}
